import axios from "axios";
import { handleActions } from "redux-actions";
import Config from "config";
import { getHeaders } from "helpers/headers";
import { AnyAction } from "interfaces/action";
import { Dispatch } from "interfaces/dispatch";
import { ThunkActionCreator } from "interfaces/thunk";

// Action Types for create project step 3

export const GET_PROJECT_STORE_REQUEST = "GET_PROJECT_STORE_REQUEST";
export const GET_PROJECT_STORE = "GET_PROJECT_STORE";
const GET_PROJECT_STORE_FAILED = "GET_PROJECT_STORE_FAILED";

export const UPDATE_PROJECT_STORE_REQUEST = "UPDATE_PROJECT_STORE_REQUEST";
export const UPDATE_PROJECT_STORE = "UPDATE_PROJECT_STORE";
const UPDATE_PROJECT_STORE_FAILED = "UPDATE_PROJECT_STORE_FAILED";

export const DELETE_PROJECT_STORE_REQUEST = "DELETE_PROJECT_STORE_REQUEST";
export const DELETE_PROJECT_STORE = "DELETE_PROJECT_STORE";
const DELETE_PROJECT_STORE_FAILED = "DELETE_PROJECT_STORE_FAILED";

export const GET_PROJECT_STORE_MAPPING_COMPARSION_REQUEST = "GET_PROJECT_STORE_MAPPING_COMPARSION_REQUEST";
export const GET_PROJECT_STORE_MAPPING_COMPARSION = "GET_PROJECT_STORE_MAPPING_COMPARSION";
const GET_PROJECT_STORE_MAPPING_COMPARSION_FAILED = "GET_PROJECT_STORE_MAPPING_COMPARSION_FAILED";

// Thunk Action Creators

const getProjectStores: ThunkActionCreator<Promise<void>> =
    (projectId: number | string, page: number = 1, limit: number = 100) =>
        async (dispatch: Dispatch) => {
            dispatch({ type: GET_PROJECT_STORE_REQUEST });

            try {
                const response = await axios.get(
                    `${Config.API_SERVICE_URL}/api/projects/projectStores/${projectId}?page=${page}&limit=${limit}`,
                    getHeaders({ "Content-Type": "application/json" })
                );

                dispatch({
                    type: GET_PROJECT_STORE,
                    payload: {
                        data: response.data.data,           
                        totalRecords: response.data.totalRecords,
                    },
                });
            } catch (error) {
                dispatch({ type: GET_PROJECT_STORE_FAILED });
            }
        };

const updateStoreDeadline: ThunkActionCreator<Promise<void>> =
    (projectId: number | string, storeIds: number[]) => async (dispatch: Dispatch) => {
        dispatch({ type: UPDATE_PROJECT_STORE_REQUEST });

        try {
            const response = await axios.patch(
                `${Config.API_SERVICE_URL}/api/store2projects/${projectId}/updateDeadlines`,
                { storeIds },
                getHeaders({
                    "Content-Type": "application/json",
                })
            );

            dispatch({
                type: UPDATE_PROJECT_STORE,
                payload: response.data,
            });
        } catch (error) {
            dispatch({ type: UPDATE_PROJECT_STORE_FAILED });
        }
    };

const deleteProjectStores: ThunkActionCreator<Promise<void>> =
  (
    projectId: number | string,
    storeData: { id: number; store_id: number; project_id: number } | Array<{ id: number; store_id: number; project_id: number }>
  ) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: DELETE_PROJECT_STORE_REQUEST });

    try {
      const response = await axios.delete(
        `${Config.API_SERVICE_URL}/api/projects/projectMappedStore/${projectId}`,
        {
          data: Array.isArray(storeData) ? storeData : [storeData],
          ...getHeaders({
            "Content-Type": "application/json",
          }),
        }
      );

      dispatch({
        type: DELETE_PROJECT_STORE,
        payload: response.data,
      });
    } catch (error) {
      dispatch({ type: DELETE_PROJECT_STORE_FAILED });
      throw error;
    }
  };

const getProjectStoreMappingComparison: ThunkActionCreator<Promise<void>> =
    (projectId: number | string) =>
    async (dispatch: Dispatch) => {
        dispatch({ type: GET_PROJECT_STORE_MAPPING_COMPARSION_REQUEST });

        try {
            const response = await axios.get(
                `${Config.API_SERVICE_URL}/api/projects/projectStores/${projectId}/mapping-comparsion`,
                getHeaders({ "Content-Type": "application/json" })
            );

            dispatch({
                type: GET_PROJECT_STORE_MAPPING_COMPARSION,
                payload: response.data,
            });
        } catch (error) {
            dispatch({ type: GET_PROJECT_STORE_MAPPING_COMPARSION_FAILED });
            throw error;
        }
    };


export { getProjectStores, updateStoreDeadline, deleteProjectStores, getProjectStoreMappingComparison };

// Interfaces
interface GetProjectStores {
    readonly id?: number;
    readonly project_id: number;
    readonly store_id: number;
    readonly validation_deadline: string;
    readonly storeNumber: number;
    readonly address: string;
    readonly updateStoreDeadline: []
}

export interface GetProjectStoresState {
    error?: boolean;
    loading?: boolean;
    content?: GetProjectStores[];
    totalRecords?: number;
    updateLoading?: boolean;
    updateError?: boolean;
    updateContent?: any;

    deleteLoading: boolean;
    deleteError: boolean;
    deleteContent: any;

    mappingComparsionLoading?: boolean;
    mappingComparsionError?: boolean;
    mappingComparsion?: {
        hasMissingMappings: boolean;
        missingCount: number;
        missingStoreIds: number[];
        missingStoreNumbers: Array<number | string>;
        missingStores: Array<{ project_id: number; store_id: number; store_number: number | string }>;
    };
}

// Initial State
const initialState: GetProjectStoresState = {
    loading: false,
    error: false,
    content: undefined,
    updateLoading: false,
    updateError: false,
    updateContent: undefined,

    deleteLoading: false,
    deleteError: false,
    deleteContent: undefined,

    mappingComparsionLoading: false,
    mappingComparsionError: false,
    mappingComparsion: undefined,
};

// Reducer Actions
const actions = {
    //  Get Project Stores
    [GET_PROJECT_STORE_REQUEST]: (state: GetProjectStoresState) => ({
        ...state,
        loading: true,
        error: false,
    }),
    [GET_PROJECT_STORE]: (
        state: GetProjectStoresState,
        action: AnyAction
    ): GetProjectStoresState => ({
        ...state,
        loading: false,
        content: Array.isArray(action.payload.data)
            ? action.payload.data
            : [],
        totalRecords: action.payload.totalRecords || 0,
    }),
    [GET_PROJECT_STORE_FAILED]: (state: GetProjectStoresState) => ({
        ...state,
        loading: false,
        error: true,
        content: undefined,
    }),

    // Update Store Deadline
    [DELETE_PROJECT_STORE_REQUEST]: (state: GetProjectStoresState) => ({
        ...state,
        deleteLoading: true,
        deleteError: false,
    }),

    [DELETE_PROJECT_STORE]: (
        state: GetProjectStoresState,
        action: AnyAction
    ): GetProjectStoresState => ({
        ...state,
        deleteLoading: false,
        deleteContent: action.payload,
    }),

    [DELETE_PROJECT_STORE_FAILED]: (state: GetProjectStoresState) => ({
        ...state,
        deleteLoading: false,
        deleteError: true,
    }),

    [GET_PROJECT_STORE_MAPPING_COMPARSION_REQUEST]: (state: GetProjectStoresState) => ({
        ...state,
        mappingComparsionLoading: true,
        mappingComparsionError: false,
    }),

    [GET_PROJECT_STORE_MAPPING_COMPARSION]: (
        state: GetProjectStoresState,
        action: AnyAction
    ): GetProjectStoresState => ({
        ...state,
        mappingComparsionLoading: false,
        mappingComparsionError: false,
        mappingComparsion: action.payload,
    }),

    [GET_PROJECT_STORE_MAPPING_COMPARSION_FAILED]: (state: GetProjectStoresState) => ({
        ...state,
        mappingComparsionLoading: false,
        mappingComparsionError: true,
        }),
};

// Reducer Export

export default handleActions<GetProjectStoresState, AnyAction>(actions, initialState);
